// src/components/Canvas.jsx
import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import Logo from './Logo';

const Canvas = ({ password }) => {
  const [content, setContent] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    // Connect to the Socket.IO server with authentication
    socketRef.current = io({
      auth: {
        token: password,
      },
    });

    socketRef.current.on('connect', () => {
      console.log('Connected to server!');
      setIsConnected(true);
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
    });
    
    // Listen for the initial full state of the canvas
    socketRef.current.on('initial_content', (initialContent) => {
        setContent(initialContent);
    });

    // Listen for single new items from other clients
    socketRef.current.on('update_content', (newItem) => {
      setContent((prevContent) => [...prevContent, newItem]);
    });
    
    // Listen for the canvas cleared event
    socketRef.current.on('canvas_cleared', () => {
        setContent([]);
    });

    // Clean up on component unmount
    return () => {
      socketRef.current.disconnect();
    };
  }, [password]);

  const handlePaste = (event) => {
    event.preventDefault();
    const items = event.clipboardData.items;

    // Get mouse position relative to the canvas
    const canvasBounds = canvasRef.current.getBoundingClientRect();
    const x = event.clientX - canvasBounds.left;
    const y = event.clientY - canvasBounds.top;

    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        const reader = new FileReader();
        reader.onload = (e) => {
          const newItem = {
            id: Date.now(),
            type: 'image',
            value: e.target.result,
            position: { x, y }
          };
          setContent((prev) => [...prev, newItem]);
          socketRef.current.emit('paste_content', newItem);
        };
        reader.readAsDataURL(file);
      } else if (item.type.indexOf('text') !== -1) {
        item.getAsString((text) => {
          const newItem = {
            id: Date.now(),
            type: 'text',
            value: text,
            position: { x, y }
          };
          setContent((prev) => [...prev, newItem]);
          socketRef.current.emit('paste_content', newItem);
        });
      }
    }
  };

  const handleClearCanvas = () => {
    socketRef.current.emit('clear_canvas');
  };

  return (
    <div className="h-screen w-screen flex flex-col">
      <header className="flex-shrink-0 bg-white shadow-md z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
                <div className="flex items-center">
                    <Logo className="h-8 w-8" />
                    <h1 className="ml-3 font-display text-xl font-semibold">Shared Canvas</h1>
                    <span className={`ml-4 h-3 w-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    <span className="ml-2 text-sm text-gray-500">{isConnected ? 'Connected' : 'Disconnected'}</span>
                </div>
                <button
                    onClick={handleClearCanvas}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                    Clear Canvas
                </button>
            </div>
        </div>
      </header>
      <main ref={canvasRef} onPaste={handlePaste} className="flex-grow relative canvas-grid" tabIndex="0">
        <div className="absolute top-0 left-0 p-4 text-gray-400 text-sm font-semibold">
            Click here and press Ctrl+V or Cmd+V to paste content
        </div>
        {content.map((item) => {
            if (item.type === 'text') {
                return (
                    <div key={item.id} style={{ top: item.position.y, left: item.position.x }} className="absolute bg-white p-3 rounded-lg shadow-lg max-w-md">
                        <pre className="whitespace-pre-wrap font-sans text-brand-dark">{item.value}</pre>
                    </div>
                );
            }
            if (item.type === 'image') {
                return (
                    <div key={item.id} style={{ top: item.position.y, left: item.position.x }} className="absolute p-1 bg-white rounded-lg shadow-lg">
                        <img src={item.value} alt="pasted content" className="max-w-xs max-h-80 object-contain" />
                    </div>
                );
            }
            return null;
        })}
      </main>
    </div>
  );
};

export default Canvas;